const SHEET_CLASS = 'size-sheet';

let sheet = null;

function getCart() {
    return document.querySelector('cart-element')?.constructor || null;
}

function closeSheet() {
    if (!sheet) return;

    sheet.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('size-sheet--open');

    const owner = sheet.owner;
    sheet.owner = null;
    owner?.focusTrigger();
}

function buildSheet() {
    if (sheet) return sheet;

    sheet = document.createElement('div');
    sheet.className = SHEET_CLASS;
    sheet.setAttribute('aria-hidden', 'true');

    const backdrop = document.createElement('div');
    backdrop.className = `${SHEET_CLASS}--backdrop`;

    const panel = document.createElement('div');
    panel.className = `${SHEET_CLASS}--panel`;
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');

    const header = document.createElement('div');
    header.className = `${SHEET_CLASS}--header`;

    const title = document.createElement('p');
    title.className = `${SHEET_CLASS}--title`;

    const close = document.createElement('button');
    close.type = 'button';
    close.className = `${SHEET_CLASS}--close`;
    close.innerHTML =
        '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false"><path d="M3 3 17 17M17 3 3 17" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';

    const body = document.createElement('div');
    body.className = `${SHEET_CLASS}--body`;

    header.append(title, close);
    panel.append(header, body);
    sheet.append(backdrop, panel);
    document.body.appendChild(sheet);

    backdrop.addEventListener('click', closeSheet);
    close.addEventListener('click', closeSheet);
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeSheet();
    });

    body.addEventListener('click', (event) => {
        const option = event.target.closest('[data-size]');
        const owner = sheet.owner;
        if (!option || !owner) return;

        closeSheet();
        owner.addToCart(option);
    });

    sheet.refs = { title, body, close };

    return sheet;
}

function openSheet(owner) {
    const el = buildSheet();
    const { title, body, close } = el.refs;

    title.textContent = owner.dataset.title || '';
    body.replaceChildren(owner.optionsList.cloneNode(true));

    el.owner = owner;
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('size-sheet--open');
    requestAnimationFrame(() => close.focus());
}

class ProductSizePicker extends HTMLElement {
    connectedCallback() {
        this.optionsList = this.querySelector('.product-card--sizes--list');
        this.trigger = this.querySelector('.product-card--sizes--trigger');

        const data = this.querySelector('.product-card--sizes--data');
        this.variants = data ? JSON.parse(data.textContent) : [];
        this.size_index = Number.parseInt(this.dataset.sizePosition, 10) - 1;

        this.card = this.closest('.product-card--root');
        const swatch_position = Number.parseInt(this.card?.dataset.swatchOptionPosition, 10);
        this.color_index = Number.isInteger(swatch_position) ? swatch_position - 1 : -1;

        if (this.card && this.color_index >= 0) {
            this.card.addEventListener('change', (event) => {
                if (event.target.classList.contains('swatch--input')) this.refresh();
            });
            this.refresh();
        }

        this.addEventListener('click', (event) => this.onClick(event));
    }

    disconnectedCallback() {
        if (sheet?.owner === this) closeSheet();
    }

    focusTrigger() {
        this.trigger?.focus();
    }

    get selected_color() {
        if (this.color_index < 0 || !this.card) return null;
        return this.card.querySelector('.swatch--input:checked')?.value || null;
    }

    resolveVariant(size) {
        const color = this.selected_color;
        let fallback = null;

        for (const variant of this.variants) {
            if (variant.options[this.size_index] !== size) continue;
            if (color && variant.options[this.color_index] !== color) continue;
            if (variant.available) return variant;
            if (!fallback) fallback = variant;
        }

        return fallback;
    }

    refresh() {
        this.optionsList?.querySelectorAll('[data-size]').forEach((button) => {
            const variant = this.resolveVariant(button.dataset.size);
            button.disabled = !variant?.available;
            button.setAttribute('aria-disabled', String(button.disabled));
        });
    }

    onClick(event) {
        const option = event.target.closest('[data-size]');
        const trigger = event.target.closest('.product-card--sizes--trigger');
        if (!option && !trigger) return;

        event.preventDefault();
        event.stopPropagation();

        if (trigger) {
            openSheet(this);
            return;
        }

        this.addToCart(option);
    }

    async addToCart(button) {
        const variant = this.resolveVariant(button.dataset.size);
        if (!variant?.available) return;

        const Cart = getCart();
        const request = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                items: [{ id: variant.id, quantity: 1 }],
                sections: Cart ? Cart.sections : [],
            }),
        };

        this.setAttribute('aria-busy', 'true');

        try {
            if (Cart) {
                await Cart.addItems(request, button);
            } else {
                const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, request);
                if (!response.ok) throw new Error(response.statusText);
                window.location.assign(window.Shopify.routes.cart);
            }
        } catch (error) {
            console.error('Size picker add to cart failed:', error);
        } finally {
            this.setAttribute('aria-busy', 'false');
        }
    }
}

if (!customElements.get('product-size-picker')) {
    customElements.define('product-size-picker', ProductSizePicker);
}
